import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {
  ApiResponseGetBatteryStatusResponse,
  ApiResponseGetMaintenanceRecordResponse,
  BatteryStatus,
  CreatedMaintenanceRecordRequest,
  type GetMaintenanceRecordResponse,
} from '@di-shared/api/di/__generated';
import {diApi} from '@di-shared/api/di';
import {parseDateISO} from '@shared/dateUtils';

export enum BatteryLevelRating {
  GOOD = 'GOOD',
  NORMAL_GOOD = 'NORMAL-GOOD',
  NORMAL_WARNING = 'NORMAL-WARNING',
  WARNING = 'WARNING',
}

export enum ExpectedDischargeDaysGauge {
  Gauge1 = 1,
  Gauge2,
  Gauge3,
  Gauge4,
  Gauge5,
  Gauge6,
}

export enum ExternalDevicePowerConsumptionRating {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY-HIGH',
}

function useBatteryStatus(vin: string) {
  const [{data: batteryStatusData, refetch: refetchBatteryStatusData}, {data: exchangeRecordData, refetch: refetchExchangeRecordData}] =
    useSuspenseQueries({
      queries: [
        {
          queryKey: [getQueryKey(diApi.battery.getBatteryStatus3), vin],
          queryFn: () =>
            diApi.battery.getBatteryStatus3({
              vin,
            }),
          select: (data: ApiResponseGetBatteryStatusResponse) => data.data?.batteryStatus as BatteryStatus,
        },
        {
          queryKey: [
            getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord),
            vin,
            CreatedMaintenanceRecordRequest.sestCode._18,
          ],
          queryFn: () =>
            diApi.maintenanceRecordController.getMostRecentMaintenanceRecord({
              vin,
              sestCode: CreatedMaintenanceRecordRequest.sestCode._18,
            }),
          select: (data: ApiResponseGetMaintenanceRecordResponse) => data.data as GetMaintenanceRecordResponse,
        },
      ],
    });
  /**
   * 방전 예상 기간
   * 5일 이상 / 4~5일 / 3~4일 / 2~3일 / 1~2일 / 방전 위험
   */
  const expectedDischargeDaysGauge = getExpectedDischargeDaysGauge(batteryStatusData.expectedDischargeDays as number);

  const {drivingTime} = batteryStatusData;
  const drivingTimeHour = (drivingTime?.dailyAverage && Math.floor(drivingTime.dailyAverage / 60)) ?? 0;
  const drivingTimeMin = (drivingTime?.dailyAverage && Math.round(drivingTime.dailyAverage % 60)) ?? 0;
  const drivingTimeAverageHour = (drivingTime?.dailyAverageForSameCars && Math.floor(drivingTime.dailyAverageForSameCars / 60)) ?? 0;
  const drivingTimeAverageMin = (drivingTime?.dailyAverageForSameCars && Math.round(drivingTime.dailyAverageForSameCars % 60)) ?? 0;
  const drivingTimePercentage = (drivingTime?.ratio && Math.round(drivingTime.ratio)) ?? 100;
  /**
   * # 방전 위험도: dischargeRisk
   * 방전 위험도 ( 양호 vs 방전 위험 감지 vs 확인 불가 case)
   * (i) 배터리 상태 확인 가능
   * Boolean statusDetected : true
   * 그 외 응답값 존재
   *
   * (ii) 배터리 상태 확인 불가능
   * Boolean statusDetected : false
   * aged를 제외한 응답값 없음
   * 내 운전량: drivingTime.dailyAverage
   * 평균 운전량: drivingTime.dailyAverageForSameCars -> 퍼센트: drivingTime.ratio
   * 배터리 잔량 등급: level.rating
   * 긴급출동 연락처
   * 방전 예상기간: expectedDischargeDays
   * 업데이트 날짜: 현재일자가 표출
   * 외부 전자기기 감지 여부: externalDevice.detected
   * 전력 소모 등급: externalDevice.powerConsumptionRating
   * ----
   * # 노후화 진단
   * 노후화 case: aged
   */
  return {
    ...batteryStatusData,
    expectedDischargeDaysGauge,
    powerConsumptionRating: batteryStatusData?.externalDevice?.powerConsumptionRating as ExternalDevicePowerConsumptionRating,
    drivingTimeHour,
    drivingTimeMin,
    drivingTimeAverageHour,
    drivingTimeAverageMin,
    drivingTimePercentage,
    replaceDate: exchangeRecordData?.replaceDate ? parseDateISO(exchangeRecordData.replaceDate) : null,
    refetchBatteryStatusData,
    refetchExchangeRecordData,
  };
}

function getExpectedDischargeDaysGauge(expectedDischargeDays: number) {
  if (expectedDischargeDays > 5) {
    return ExpectedDischargeDaysGauge.Gauge6;
  }
  if (4 < expectedDischargeDays && expectedDischargeDays <= 5) {
    return ExpectedDischargeDaysGauge.Gauge5;
  }
  if (3 < expectedDischargeDays && expectedDischargeDays <= 4) {
    return ExpectedDischargeDaysGauge.Gauge4;
  }
  if (2 < expectedDischargeDays && expectedDischargeDays <= 3) {
    return ExpectedDischargeDaysGauge.Gauge3;
  }
  if (1 < expectedDischargeDays && expectedDischargeDays <= 2) {
    return ExpectedDischargeDaysGauge.Gauge2;
  }
  return ExpectedDischargeDaysGauge.Gauge1;
}

export default useBatteryStatus;
